import React, { useEffect, useState } from "react";
import { getConsumerSummary } from "../api/consumerService";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
} from "recharts";
import {
    FiUsers,
    FiGrid,
} from "react-icons/fi";

const ConsumerDashboard = () => {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchSummary();
    }, []);

    const fetchSummary = async () => {
        try {
            const data = await getConsumerSummary();
            setSummary(data);
        } catch (err) {
            console.error("Failed to load summary", err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64 text-gray-500">
                Loading dashboard...
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-8">
            {/* HEADER */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">
                        Consumer Dashboard
                    </h2>
                    <p className="text-sm text-gray-500">
                        Overall summary of all registered consumers
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {/* TOTAL CARD (Distinct Style) */}
                <div className="rounded-xl p-5 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-300 flex items-center justify-between border border-sky-200 bg-gradient-to-br from-sky-100 via-blue-100 to-indigo-100 text-slate-800">
                    <div>
                        <p className="text-sm text-slate-800 font-bold">Total Applications</p>
                        <p className="text-3xl font-bold text-blue-700 mt-1">
                            {summary?.total ?? 0}
                        </p>
                    </div>
                    <div className="bg-blue-100 text-blue-700 p-3 rounded-full shadow-sm">
                        <FiUsers size={24} />
                    </div>
                </div>


                {/* DIVISION CARDS (Soft Gradients) */}
                {summary?.divisions?.map((d, i) => (
                    <div
                        key={d.division}
                        className={`rounded-xl p-5 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-300 flex items-center justify-between border
        ${i % 3 === 0
                                ? "bg-gradient-to-br from-indigo-50 via-indigo-100 to-indigo-200 border-indigo-200"
                                : i % 3 === 1
                                    ? "bg-gradient-to-br from-sky-50 via-sky-100 to-sky-200 border-sky-200"
                                    : "bg-gradient-to-br from-blue-50 via-blue-100 to-blue-200 border-blue-200"
                            }`}
                    >
                        <div>
                            <p className="text-sm text-slate-600 font-medium">{d.division}</p>
                            <p className="text-3xl font-bold text-slate-800 mt-1">{d.count}</p>
                        </div>
                        <div
                            className={`p-3 rounded-full shadow-sm ${i % 3 === 0
                                ? "bg-indigo-200 text-indigo-700"
                                : i % 3 === 1
                                    ? "bg-sky-200 text-sky-700"
                                    : "bg-blue-200 text-blue-700"
                                }`}
                        >
                            <FiGrid size={22} />
                        </div>
                    </div>
                ))}
            </div>




            {/* CHART */}
            <div className="bg-white rounded-xl shadow border p-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">
                        Division-wise Distribution
                    </h3>
                </div>

                <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={summary?.divisions || []}
                            margin={{ top: 20, right: 20, left: 0, bottom: 80 }} // ⬅ important
                        >
                            <CartesianGrid strokeDasharray="3 3" />

                            <XAxis
                                dataKey="division"
                                interval={0}                 // ⬅ show all labels
                                angle={-35}                  // ⬅ rotate
                                textAnchor="end"
                                height={70}
                                tick={{ fontSize: 12 }}
                                tickFormatter={(value) =>
                                    value.length > 15 ? value.slice(0, 15) + "…" : value
                                }
                            />

                            <YAxis allowDecimals={false} />

                            <Tooltip />

                            <Bar
                                dataKey="count"
                                fill="#2563eb"
                                radius={[6, 6, 0, 0]}
                            />
                        </BarChart>
                    </ResponsiveContainer>

                </div>
            </div>
        </div>
    );
};

export default ConsumerDashboard;
